/**
 * Tagada Rx — the two things an affiliate must complete before Tagada can
 * pay them: verify the legal representative, then declare a bank account.
 *
 * Both gates live on the account (a brand with several Rx stores does this
 * once), which is why this card takes no storeId.
 */

import { CheckCircle2, Circle, Loader2, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useRxPayoutProfile } from '@/lib/api/rx';

import { RxBankAccountForm } from './rx-bank-account-form';
import { RxKycStep } from './rx-kyc-step';

function StepHeader({
  index,
  title,
  done,
}: {
  index: number;
  title: string;
  done: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      {done ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : (
        <Circle className="text-muted-foreground h-4 w-4" />
      )}
      <span className="text-sm font-semibold">
        {index}. {title}
      </span>
    </div>
  );
}

export function RxPayoutSetup() {
  const { t } = useTranslation('rx');
  const profileQuery = useRxPayoutProfile({ poll: true });

  if (profileQuery.isLoading || !profileQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="text-primary h-4 w-4" />
            {t('payoutSetup.title')}
          </CardTitle>
          <CardDescription>{t('payoutSetup.loading')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const profile = profileQuery.data;
  const refetch = () => {
    profileQuery.refetch();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Wallet className="text-primary h-4 w-4" />
          {t('payoutSetup.title')}
        </CardTitle>
        <CardDescription>
          {profile.payoutsReady
            ? t('payoutSetup.descriptionReady')
            : t('payoutSetup.descriptionPending')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-3">
          <StepHeader index={1} title={t('payoutSetup.step1')} done={profile.kycCleared} />
          <RxKycStep profile={profile} onChanged={refetch} />
        </div>

        <div className="space-y-3 border-t pt-4">
          <StepHeader index={2} title={t('payoutSetup.step2')} done={profile.bankReady} />
          {profile.kycCleared ? (
            <RxBankAccountForm profile={profile} onSaved={refetch} />
          ) : (
            <p className="text-muted-foreground text-xs">{t('payoutSetup.gate')}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
